import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AreaChart, Area,
    XAxis, YAxis, Tooltip,
    CartesianGrid,
    ResponsiveContainer
} from "recharts";

import "./TenantDashboard.css";
import CommonDateInput from "../../../components/CommonDateInput";
import { DotLoaderMark } from "../../../components/Loader";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import InventoryIcon from "@mui/icons-material/Inventory";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PaymentsIcon from "@mui/icons-material/Payments";
import SpeedIcon from "@mui/icons-material/Speed";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import LayersIcon from "@mui/icons-material/Layers";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import {
    fetchPOSummary,
    fetchAsnSummary,
    fetchSoSummary,
    fetchShipmentSummary,
    fetchGrnSummary,
    fetchInventorySummary,
    exportInventoryItemSummary
} from "../../../services/dashboard/dashboard";
import { toast } from "../../../components/AppToast";
import { fetchFacilities } from "../../../redux/action/facility.action";
import { filterDashboardTabs } from "./dashboardTabs";

/* ---------------- COLORS ---------------- */
const STATUS_COLORS = {
    New: "#3B82F6",
    Approved: "#6366F1",
    Pending: "#F59E0B",
    Invoiced: "#0EA5E9",
    "In Process": "#22C55E",
    Completed: "#16A34A",
    Canceled: "#EF4444",
    Packed: "#8B5CF6",
    "In Transit": "#0EA5E9",
    Delivered: "#16A34A",
    Partial: "#F97316",
    Available: "#16A34A",
    Allocated: "#3B82F6",
    Picked: "#0EA5E9",
    "On Hold": "#F59E0B",
    "No Stock": "#94A3B8"
};

const THEME_BLUE = "#1e71bb";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_WEIGHTS = [0.055, 0.062, 0.07, 0.074, 0.082, 0.088, 0.09, 0.098, 0.105, 0.102, 0.092, 0.082];
const SERIES_PHASE = [0.4, 2.1, 3.7, 1.3, 5.0];

const FULFILLED = new Set(["Completed", "Delivered", "Invoiced"]);
const EXCEPTION = new Set(["Canceled"]);
const WATCH = new Set(["Pending", "Partial"]);

/* ---------------- CONFIG ---------------- */
const DASHBOARD_CONFIG = {
    PO: {
        title: "Purchase Order Dashboard",
        subtitle: "Procurement overview & status tracking",
        totalLabel: "Purchase Orders",
        spendLabel: "Total Procurement Spend",
        avgLabel: "Average PO Value",
        lineTitle: "Procurement Trend",
        unit: "orders"
    },
    ASN: {
        title: "Advanced Shipping Notice Dashboard",
        subtitle: "Inbound shipment & ASN tracking",
        totalLabel: "ASN",
        spendLabel: "Total ASN Quantity",
        avgLabel: "ASN per Year",
        lineTitle: "ASN Trend",
        unit: "notices"
    },
    SO: {
        title: "Sales Order Dashboard",
        subtitle: "Outbound sales & fulfillment tracking",
        totalLabel: "Sales Orders",
        spendLabel: "Total Order Quantity",
        avgLabel: "Average Order Quantity",
        lineTitle: "Sales Order Trend",
        unit: "orders"
    },
    SHIPMENT: {
        title: "Shipment Dashboard",
        subtitle: "Outbound shipment & delivery tracking",
        totalLabel: "Shipments",
        spendLabel: "Total Shipped Quantity",
        avgLabel: "Average Shipment Quantity",
        lineTitle: "Shipment Trend",
        unit: "shipments"
    },
    GRN: {
        title: "Goods Receipt Dashboard",
        subtitle: "Inbound goods receipt & inventory update",
        totalLabel: "Goods Receipts",
        spendLabel: "Total Received Quantity",
        avgLabel: "GRN per Year",
        lineTitle: "GRN Trend",
        unit: "receipts"
    },
    INVENTORY: {
        title: "Inventory Visibility Dashboard",
        subtitle: "Warehouse inventory & stock visibility",
        totalLabel: "Inventory Records",
        spendLabel: "Total Stock On Hand",
        avgLabel: "On Hold",
        lineTitle: "Inventory Trend",
        unit: "records"
    }
};

const VIEW_ICONS = {
    PO: ShoppingCartIcon,
    ASN: LocalShippingIcon,
    SO: StorefrontIcon,
    SHIPMENT: FlightTakeoffIcon,
    GRN: InventoryIcon,
    INVENTORY: VisibilityIcon,
};

const VIEW_TABS = [
    { key: "PO", label: "Purchase Orders" },
    { key: "ASN", label: "Advanced Shipping Notice" },
    { key: "SO", label: "Sales Orders" },
    { key: "SHIPMENT", label: "Shipments" },
    { key: "GRN", label: "Goods Receipt" },
    { key: "INVENTORY", label: "Inventory Visibility" },
];

const formatCurrency = (v = 0) =>
    `$ ${Number(v || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;

const formatQty = (v = 0) =>
    Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const compareText = (left, right) =>
    String(left ?? "").localeCompare(String(right ?? ""), undefined, { numeric: true, sensitivity: "base" });

const compareOwnerThenSku = (left, right) => {
    const byFacility = compareText(left?.facilityName, right?.facilityName);
    if (byFacility !== 0) return byFacility;
    const byOwner = compareText(left?.owner, right?.owner);
    if (byOwner !== 0) return byOwner;
    return compareText(left?.item, right?.item);
};

/** Percentage of stock on hand, rounded so a real quantity never shows as 0%. */
const sharePercents = (amounts, total) => {
    const parts = Array.isArray(amounts) ? amounts : [];
    if (!total) return parts.map(() => 0);
    return parts.map((amount) => {
        const qty = Number(amount) || 0;
        if (qty <= 0) return 0;
        const rounded = Math.round(((qty / total) * 100) * 100) / 100;
        return rounded > 0 ? rounded : 0.01;
    });
};

const formatInventoryPct = (pct) => {
    const value = Number(pct) || 0;
    if (value <= 0) return "0%";
    if (value >= 100) return "100%";
    return `${value.toFixed(2).replace(/\.00$/, "")}%`;
};

const compactNumber = (v = 0) => {
    const n = Number(v) || 0;
    const abs = Math.abs(n);
    if (abs >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${(n / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
    return `${Math.round(n)}`;
};

const statusStage = (name, view) => {
    if (view === "INVENTORY") {
        if (name === "On Hold") return "waiting";
        if (name === "Available") return "closed";
        return "open";
    }
    if (EXCEPTION.has(name)) return "cancelled";
    if (WATCH.has(name)) return "waiting";
    if (FULFILLED.has(name)) return "closed";
    return "open";
};

const statusGroup = (name, view) => {
    if (view === "INVENTORY") {
        if (name === "Available") return "Fulfilled";
        if (name === "On Hold") return "Exception";
        if (name === "No Stock") return "Empty";
        return "Pipeline";
    }
    if (EXCEPTION.has(name)) return "Exception";
    if (FULFILLED.has(name)) return "Fulfilled";
    return "Pipeline";
};

const STAGE_META = {
    open: { label: "Open", className: "dash-pill dash-pill-open" },
    waiting: { label: "Waiting", className: "dash-pill dash-pill-watch" },
    closed: { label: "Closed", className: "dash-pill dash-pill-good" },
    cancelled: { label: "Cancelled", className: "dash-pill dash-pill-critical" },
};

/** Spread an annual total across months with a different wave per series. */
const distribute = (total, phase) => {
    const raw = MONTH_WEIGHTS.map((weight, index) =>
        weight * (1 + 0.14 * Math.sin((index + phase) * 0.85))
    );
    const sum = raw.reduce((acc, value) => acc + value, 0) || 1;
    const target = Number(total) || 0;
    const values = raw.map((value) => Math.round((value / sum) * target));
    const drift = Math.round(target) - values.reduce((acc, value) => acc + value, 0);
    let peak = 0;
    raw.forEach((value, index) => {
        if (value > raw[peak]) peak = index;
    });
    values[peak] = Math.max(0, values[peak] + drift);
    return values;
};

const buildTrend = ({ yearlyData, annualTotal, series }) => {
    const points = Array.isArray(yearlyData) ? yearlyData : [];

    if (points.length >= 2) {
        return {
            mode: "yearly",
            caption: "Reported yearly totals",
            data: points.map((point) => ({
                label: String(point.year ?? point.label ?? ""),
                Volume: Number(point.value) || 0,
            })),
            series: [{ key: "Volume", color: THEME_BLUE }],
        };
    }

    const annual = points.reduce((sum, point) => sum + (Number(point.value) || 0), 0) || Number(annualTotal) || 0;
    const active = (series || []).filter((item) => item.share > 0).slice(0, 4);

    if (annual <= 0) {
        return { mode: "empty", caption: "", data: [], series: [] };
    }

    const columns = (active.length ? active : [{ name: "Volume", color: THEME_BLUE, share: 1 }])
        .map((item, index) => ({
            key: item.name,
            color: item.color || THEME_BLUE,
            values: distribute(annual * (item.share || 1), SERIES_PHASE[index] || index),
        }));

    return {
        mode: "monthly",
        caption: "Monthly view of the period total",
        data: MONTHS.map((label, index) => {
            const row = { label };
            columns.forEach((column) => {
                row[column.key] = column.values[index];
            });
            return row;
        }),
        series: columns.map(({ key, color }) => ({ key, color })),
    };
};

const trendGradientId = (key) => `dash-fill-${String(key).replace(/[^a-zA-Z0-9_-]/g, "")}`;

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/** From = one month before today, To = today. */
const defaultDashboardRange = () => {
    const today = new Date();
    const from = new Date(today);
    from.setMonth(today.getMonth() - 1);
    return { from: formatLocalDate(from), to: formatLocalDate(today) };
};

const toApiDate = (value) => {
    if (!value) return "";
    const text = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return "";
    return formatLocalDate(date);
};

const TrendTooltip = ({ active, payload, label, currency }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="dash-tooltip">
            <div className="dash-tooltip-label">{label}</div>
            {payload.map((item) => (
                <div key={item.dataKey} className="dash-tooltip-row">
                    <span className="dot" style={{ background: item.color || item.stroke }} />
                    <span>{item.dataKey}</span>
                    <strong>{currency ? formatCurrency(item.value) : formatQty(item.value)}</strong>
                </div>
            ))}
        </div>
    );
};

const TenantDashboard = () => {
    const dispatch = useDispatch();
    const facilities = useSelector((state) => state.facility?.facilities || []);
    const selectedFacility = useSelector((state) => state.facility?.selectedFacility);
    const navData = useSelector((state) => state.permissions?.navData || []);
    const userDetail = useSelector((state) => state.login?.userDetail);
    const visibleTabs = useMemo(
        () => filterDashboardTabs(VIEW_TABS, { menus: navData, user: userDetail }),
        [navData, userDetail]
    );
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("PO");
    const [fromDate, setFromDate] = useState(() => defaultDashboardRange().from);
    const [toDate, setToDate] = useState(() => defaultDashboardRange().to);
    const [warehouse, setWarehouse] = useState(selectedFacility?.value || "");
    const [showAllStatuses, setShowAllStatuses] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [dataView, setDataView] = useState("PO");
    const [exportingItems, setExportingItems] = useState(false);
    const [inventoryColumnFilters, setInventoryColumnFilters] = useState({
        facilityName: "",
        owner: "",
        item: "",
        description: "",
        unitOfMeasure: "",
        stockOnHand: "",
        allocated: "",
        picked: "",
        onHold: "",
        available: "",
    });
    const requestIdRef = useRef(0);
    const appliedInventoryWarehouseRef = useRef("");

    const config = DASHBOARD_CONFIG[view];
    const dataConfig = DASHBOARD_CONFIG[dataView];

    const loadByView = async (target, params) => {
        if (target === "ASN") return fetchAsnSummary(params);
        if (target === "SO") return fetchSoSummary(params);
        if (target === "SHIPMENT") return fetchShipmentSummary(params);
        if (target === "GRN") return fetchGrnSummary(params);
        if (target === "INVENTORY") return fetchInventorySummary(params);
        return fetchPOSummary(params);
    };

    const fetchView = async (target, params) => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const data = await loadByView(target, params);
            if (requestId !== requestIdRef.current) return;
            if (target === "INVENTORY") {
                appliedInventoryWarehouseRef.current = params?.warehouse || "";
            }
            setDashboard(data);
            setDataView(target);
            setUpdatedAt(new Date());
        } catch (err) {
            if (requestId === requestIdRef.current) console.error("Dashboard Error", err);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    const buildParams = (range = {}, warehouseId = warehouse) => {
        const params = {
            from: toApiDate(range.from ?? fromDate),
            to: toApiDate(range.to ?? toDate),
        };
        if (warehouseId) params.warehouse = warehouseId;
        return params;
    };

    const resetDatesAndReload = () => {
        const range = defaultDashboardRange();
        const nextWarehouse = selectedFacility?.value || "";
        setFromDate(range.from);
        setToDate(range.to);
        setWarehouse(nextWarehouse);
        fetchView(view, buildParams(range, nextWarehouse));
    };

    useEffect(() => {
        if (!facilities.length) dispatch(fetchFacilities());
    }, [dispatch, facilities.length]);

    useEffect(() => {
        if (!visibleTabs.length) return;
        const allowed = visibleTabs.some((tab) => tab.key === view);
        if (!allowed) {
            setView(visibleTabs[0].key);
            return;
        }
        loadDashboard();
        // eslint-disable-next-line
    }, [view, visibleTabs]);

    const loadDashboard = () => {
        fetchView(view, buildParams());
    };

    const inventoryItems = useMemo(
        () => (Array.isArray(dashboard?.items) ? [...dashboard.items] : []).sort(compareOwnerThenSku),
        [dashboard?.items]
    );
    const filteredInventoryItems = useMemo(() => {
        const activeFilters = Object.entries(inventoryColumnFilters).filter(
            ([, value]) => value != null && String(value).trim() !== ""
        );
        if (activeFilters.length === 0) return inventoryItems;
        return inventoryItems.filter((item) =>
            activeFilters.every(([field, value]) => {
                const expected = String(value).trim().toLowerCase();
                const raw = item?.[field];
                return String(raw ?? "").toLowerCase().includes(expected);
            })
        );
    }, [inventoryItems, inventoryColumnFilters]);

    const updateInventoryColumnFilter = (field, value) => {
        setInventoryColumnFilters((prev) => ({ ...prev, [field]: value }));
    };

    if (!visibleTabs.length) {
        return (
            <div className="dashboard-loader">
                <p className="text-muted mb-0">No dashboard tab matches your assigned modules.</p>
            </div>
        );
    }

    if (!dashboard) {
        if (!loading) return null;
        return (
            <div className="dashboard-loader">
                <DotLoaderMark />
            </div>
        );
    }

    const refreshing = loading;
    const isCurrency = dataView === "PO";
    const formatValue = isCurrency ? formatCurrency : formatQty;

    const totalCount =
        dataView === "PO" ? dashboard.summary?.totalPO :
            dataView === "ASN" ? dashboard.summary?.totalASN :
                dataView === "SO" ? dashboard.summary?.totalSO :
                    dataView === "SHIPMENT" ? dashboard.summary?.totalShipment :
                    dataView === "INVENTORY" ? dashboard.summary?.totalInventory :
                        dashboard.summary?.totalGRN;

    const totalSpend =
        dataView === "PO" ? dashboard.summary?.totalSpend :
            dataView === "GRN" ? dashboard.summary?.totalReceivedQty :
                dashboard.summary?.totalQuantity;

    const isInventory = dataView === "INVENTORY";
    const showInventoryFilters = view === "INVENTORY";
    const onHoldQty = Number(dashboard.summary?.totalOnHold) || 0;
    const availableQty = Number(dashboard.summary?.totalAvailable) || 0;

    const avgSpend =
        dataView === "PO" ? dashboard.summary?.avgSpend :
            isInventory ? onHoldQty :
                dashboard.summary?.avgQuantity || 0;

    const statusData = (dashboard.statusPie || []).map((status) => ({
        ...status,
        value: Number(status.value) || 0,
        color: STATUS_COLORS[status.name] || "#9CA3AF"
    }));

    const totalStatus = statusData.reduce((sum, status) => sum + status.value, 0);
    const ranked = [...statusData].sort((a, b) => b.value - a.value);
    const visibleStatuses = showAllStatuses
        ? ranked
        : ranked.filter((status) => status.value > 0);
    const tableRows = visibleStatuses.length ? visibleStatuses : ranked.slice(0, 3);
    const topStatuses = ranked.filter((status) => status.value > 0).slice(0, 3);

    const inventoryStockByStatus = { Available: 0, Allocated: 0, Picked: 0, "On Hold": 0 };
    if (isInventory) {
        inventoryItems.forEach((item) => {
            const stock = Number(item.stockOnHand) || 0;
            if ((Number(item.onHold) || 0) > 0) inventoryStockByStatus["On Hold"] += stock;
            else if ((Number(item.allocated) || 0) > 0) inventoryStockByStatus.Allocated += stock;
            else if ((Number(item.picked) || 0) > 0) inventoryStockByStatus.Picked += stock;
            else if ((Number(item.available) || 0) > 0 || stock > 0) inventoryStockByStatus.Available += stock;
        });
    }
    const inventoryStockTotal = Object.values(inventoryStockByStatus).reduce((sum, value) => sum + value, 0);
    const inventoryStatusParts = isInventory
        ? tableRows
            .filter((status) => status.name !== "No Stock")
            .map((status) => ({
                ...status,
                qty: inventoryStockByStatus[status.name] || 0,
            }))
        : [];
    const inventoryShares = sharePercents(
        inventoryStatusParts.map((status) => status.qty),
        inventoryStockTotal
    );
    const inventoryMix = isInventory
        ? [
            ...inventoryStatusParts.map((status, index) => ({
                ...status,
                pct: inventoryShares[index] || 0,
            })),
            {
                name: "Stock On Hand",
                value: Number(dashboard.summary?.totalQuantity) || inventoryStockTotal,
                color: "#1e71bb",
                pct: inventoryStockTotal > 0 ? 100 : 0,
                isTotal: true,
            },
        ]
        : [];

    const pipelineCount = statusData
        .filter((status) => statusGroup(status.name, dataView) === "Pipeline")
        .reduce((sum, status) => sum + status.value, 0);
    const attentionCount = statusData
        .filter((status) => {
            const stage = statusStage(status.name, dataView);
            return stage === "waiting" || stage === "cancelled";
        })
        .reduce((sum, status) => sum + status.value, 0);

    const pipelinePct = totalStatus ? Math.round((pipelineCount / totalStatus) * 100) : 0;
    const leadStatus = topStatuses[0];
    const leadPct = totalStatus && leadStatus
        ? ((leadStatus.value / totalStatus) * 100).toFixed(0)
        : 0;

    const yearlySum = (dashboard.yearlyData || []).reduce((sum, point) => sum + (Number(point.value) || 0), 0);
    const annualTotal = yearlySum || Number(totalSpend) || 0;

    const trend = buildTrend({
        yearlyData: dashboard.yearlyData,
        annualTotal,
        series: statusData.map((status) => ({
            name: status.name,
            color: status.color,
            share: totalStatus ? status.value / totalStatus : 0,
        })),
    });

    const updatedLabel = updatedAt
        ? updatedAt.toLocaleString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "short",
            year: "numeric",
        })
        : "";

    const handleExportItemSummary = async () => {
        if (exportingItems) return;
        try {
            setExportingItems(true);
            await exportInventoryItemSummary(appliedInventoryWarehouseRef.current);
            toast.success("Your file is being generated. Track its progress and download it under: Tools > Excel Imports/Exports");
        } catch (err) {
            console.error("Export error:", err);
            toast.error(typeof err === "string" ? err : err?.message || "Export failed");
        } finally {
            setExportingItems(false);
        }
    };

    const ViewIcon = VIEW_ICONS[dataView] || ShoppingCartIcon;
    const allocatedQty = inventoryItems.reduce((sum, item) => sum + (Number(item.allocated) || 0), 0);
    const pickedQty = inventoryItems.reduce((sum, item) => sum + (Number(item.picked) || 0), 0);
    const skuIdentity = (item) => {
        const sku = String(item?.item ?? "").trim();
        return sku || String(item?.id ?? "");
    };
    const countSkus = (predicate) => {
        const seen = new Set();
        inventoryItems.forEach((item) => {
            if (predicate && !predicate(item)) return;
            const key = skuIdentity(item);
            if (key) seen.add(key);
        });
        return seen.size;
    };
    const formatSkuCount = (count) => `${formatQty(count)} ${Number(count) === 1 ? "SKU" : "SKUs"}`;
    const skuCount = countSkus();
    const allocatedSkuCount = countSkus((item) => (Number(item.allocated) || 0) > 0);
    const pickedSkuCount = countSkus((item) => (Number(item.picked) || 0) > 0);
    const onHoldSkuCount = countSkus((item) => (Number(item.onHold) || 0) > 0);
    const stockOnHandQty = Number(dashboard.summary?.totalQuantity) || 0;

    const inventoryKpis = [
        {
            key: "stock",
            label: "Stock On Hand",
            value: formatQty(stockOnHandQty),
            delta: formatSkuCount(skuCount),
            tone: "neutral",
            icon: <InventoryIcon fontSize="small" />,
        },
        {
            key: "available",
            label: "Available",
            value: formatQty(availableQty),
            delta: `across ${formatSkuCount(skuCount)}`,
            tone: availableQty > 0 ? "up" : "neutral",
            icon: <CheckCircleOutlineIcon fontSize="small" />,
        },
        {
            key: "allocated",
            label: "Allocated",
            value: formatQty(allocatedQty),
            delta: formatSkuCount(allocatedSkuCount),
            tone: "neutral",
            icon: <LayersIcon fontSize="small" />,
        },
        {
            key: "picked",
            label: "Picked",
            value: formatQty(pickedQty),
            delta: formatSkuCount(pickedSkuCount),
            tone: "neutral",
            icon: <ShoppingBasketIcon fontSize="small" />,
        },
        {
            key: "hold",
            label: "On Hold",
            value: formatQty(onHoldQty),
            delta: `${formatSkuCount(onHoldSkuCount)} on hold`,
            tone: onHoldQty > 0 ? "down" : "neutral",
            icon: <PauseCircleOutlineIcon fontSize="small" />,
        },
    ];

    const kpis = isInventory ? inventoryKpis : [
        {
            key: "total",
            label: `Total ${dataConfig.totalLabel}`,
            value: formatQty(totalCount),
            delta: `${formatQty(pipelineCount)} in pipeline`,
            tone: "neutral",
            icon: <ViewIcon fontSize="small" />,
        },
        {
            key: "spend",
            label: dataConfig.spendLabel,
            value: formatValue(totalSpend),
            delta: `${formatQty(totalCount)} ${dataConfig.unit}`,
            tone: "neutral",
            icon: <PaymentsIcon fontSize="small" />,
        },
        {
            key: "avg",
            label: dataConfig.avgLabel,
            value: formatValue(avgSpend),
            delta: isInventory
                ? `${formatQty(attentionCount)} records on hold`
                : totalCount ? `across ${formatQty(totalCount)} ${dataConfig.unit}` : "No volume yet",
            tone: isInventory && onHoldQty > 0 ? "down" : "neutral",
            icon: isInventory ? <InventoryIcon fontSize="small" /> : <SpeedIcon fontSize="small" />,
        },
        {
            key: "pipeline",
            label: isInventory ? "Available" : "Active Pipeline",
            value: formatQty(isInventory ? availableQty : pipelineCount),
            delta: isInventory
                ? `across ${formatQty(totalCount)} ${dataConfig.unit}`
                : `${pipelinePct}% of volume`,
            tone: isInventory ? (availableQty > 0 ? "up" : "neutral") : (pipelinePct >= 50 ? "up" : "neutral"),
            icon: isInventory ? <CheckCircleOutlineIcon fontSize="small" /> : <AutorenewIcon fontSize="small" />,
        },
        {
            key: "alerts",
            label: "Needs Attention",
            value: formatQty(attentionCount),
            delta: attentionCount ? "Review open items" : "All clear",
            tone: attentionCount ? "down" : "up",
            icon: <NotificationImportantIcon fontSize="small" />,
        },
    ];

    return (
        <div className="dash-container">

            <div className="dashboard-header">
                <div className="dash-toolbar">
                    <div className="dash-toolbar-copy">
                        <h2>{config.title}</h2>
                        <p>{config.subtitle}</p>
                    </div>

                    <div className="dash-filter-card">
                        <label className="dash-filter-inline">
                            <WarehouseIcon sx={{ fontSize: 15 }} />
                            <span>Warehouse</span>
                            <select
                                className="dash-warehouse-select"
                                value={warehouse}
                                onChange={(e) => setWarehouse(e.target.value)}
                                aria-label="Warehouse"
                            >
                                <option value="">All warehouses</option>
                                {warehouse && !facilities.some((facility) => facility.Facility_ID === warehouse) && (
                                    <option value={warehouse}>{selectedFacility?.label || warehouse}</option>
                                )}
                                {facilities.map((facility) => (
                                    <option key={facility._id || facility.Facility_ID} value={facility.Facility_ID}>
                                        {facility.Facility_Name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {!showInventoryFilters && (
                            <>
                        <label className="dash-filter-inline">
                            <CalendarMonthIcon sx={{ fontSize: 15 }} />
                            <span>From</span>
                            <CommonDateInput
                                name="from"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                allowPastDates
                                maxDate={toApiDate(toDate) || formatLocalDate(new Date())}
                                className="dash-date-input"
                            />
                        </label>

                        <label className="dash-filter-inline">
                            <span>To</span>
                            <CommonDateInput
                                name="to"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                allowPastDates
                                minDate={toApiDate(fromDate)}
                                maxDate={formatLocalDate(new Date())}
                                className="dash-date-input"
                            />
                        </label>
                            </>
                        )}

                        <button
                            type="button"
                            className="btn btn-primary btn-sm dash-filter-btn"
                            onClick={loadDashboard}
                        >
                            <SearchIcon sx={{ fontSize: 15 }} />
                            Apply
                        </button>

                        <button
                            type="button"
                            className="dash-filter-reset"
                            onClick={resetDatesAndReload}
                            title={showInventoryFilters ? "Reset warehouse" : "Reset"}
                            aria-label={showInventoryFilters ? "Reset warehouse" : "Reset dates"}
                        >
                            <RestartAltIcon sx={{ fontSize: 16 }} />
                        </button>
                    </div>
                </div>

                <div className="dash-view-tabs" role="tablist">
                    {visibleTabs.map((tab) => {
                        const Icon = VIEW_ICONS[tab.key];
                        const active = view === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={`dash-view-tab ${active ? "is-active" : ""}`}
                                onClick={() => {
                                    if (active) return;
                                    setView(tab.key);
                                    setShowAllStatuses(false);
                                }}
                            >
                                <span className={`dash-view-icon dash-view-icon-${tab.key.toLowerCase()}`}>
                                    <Icon sx={{ fontSize: 16 }} />
                                </span>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={`dash-body ${refreshing ? "is-refreshing" : ""}`} aria-busy={refreshing}>
            {refreshing && <div className="dash-progress" aria-hidden><span /></div>}
            <div className="dash-body-inner" key={`${dataView}-${updatedAt ? updatedAt.getTime() : 0}`}>
            <div className="dash-live">
                <div className="dash-live-main">
                    <span className="dash-live-badge">
                        <span className="dash-live-dot" />
                        Live
                    </span>
                    <span>{formatQty(totalCount)} {dataConfig.totalLabel.toLowerCase()}</span>
                    <span className="dash-live-sep" />
                    <span>{isCurrency ? formatCurrency(totalSpend) : `${formatQty(totalSpend)} qty`}</span>
                    {leadStatus && (
                        <>
                            <span className="dash-live-sep" />
                            <span>{leadStatus.name} {leadPct}%</span>
                        </>
                    )}
                </div>
                {updatedLabel && <span className="dash-live-time">Updated {updatedLabel}</span>}
            </div>

            <div className="dash-kpi-row">
                {kpis.map((kpi) => (
                    <div className="dash-kpi" key={kpi.key}>
                        <div className="dash-kpi-top">
                            <span className={`dash-kpi-icon dash-kpi-icon-${kpi.tone}`}>{kpi.icon}</span>
                            <span className="dash-kpi-label">{kpi.label}</span>
                        </div>
                        <div className="dash-kpi-value">{kpi.value}</div>
                        <div className={`dash-kpi-delta dash-kpi-delta-${kpi.tone}`}>
                            {kpi.tone === "up" && <ArrowUpwardIcon sx={{ fontSize: 13 }} />}
                            {kpi.delta}
                        </div>
                    </div>
                ))}
            </div>

            {!isInventory && (
            <div className="dash-panel">
                <div className="dash-panel-head">
                    <div>
                        <h4 className="card-title">Status Overview</h4>
                        <p className="dash-muted">Count and share of each document status</p>
                    </div>
                    <button
                        type="button"
                        className="dash-text-btn"
                        onClick={() => setShowAllStatuses((open) => !open)}
                    >
                        {showAllStatuses ? "Active only" : "View all statuses"}
                    </button>
                </div>

                <div className="dash-table-wrap">
                    <table className="dash-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Count</th>
                                <th>Share</th>
                                <th>Group</th>
                                <th>Stage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="dash-table-empty">No statuses in this range.</td>
                                </tr>
                            )}
                            {tableRows.map((status) => {
                                const pct = totalStatus ? (status.value / totalStatus) * 100 : 0;
                                const stage = STAGE_META[statusStage(status.name, dataView)];
                                return (
                                    <tr key={status.name}>
                                        <td>
                                            <span className="dash-status-name">
                                                <span className="dot" style={{ background: status.color }} />
                                                {status.name}
                                            </span>
                                        </td>
                                        <td className="dash-num">{formatQty(status.value)}</td>
                                        <td>
                                            <div className="dash-share">
                                                <div className="status-bar-bg">
                                                    <div
                                                        className="status-bar-fill"
                                                        style={{ width: `${pct}%`, background: status.color }}
                                                    />
                                                </div>
                                                <span>{pct.toFixed(0)}%</span>
                                            </div>
                                        </td>
                                        <td className="dash-muted-cell">{statusGroup(status.name, dataView)}</td>
                                        <td><span className={stage.className}>{stage.label}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {isInventory ? (
                <div className="dash-panel inv-board">
                    <div className="inv-board-head">
                        <div>
                            <h4 className="card-title">Item Summary</h4>
                            <p className="dash-muted">{formatQty(filteredInventoryItems.length)} inventory records</p>
                        </div>
                        <div className="inv-board-actions">
                            <button
                                type="button"
                                className="dash-text-btn"
                                onClick={() => setShowAllStatuses((open) => !open)}
                            >
                                {showAllStatuses ? "Active only" : "All statuses"}
                            </button>
                            <button
                                type="button"
                                className="dash-export-btn"
                                onClick={handleExportItemSummary}
                                disabled={exportingItems}
                            >
                                <i className="bi bi-download"></i>
                                Export
                            </button>
                        </div>
                    </div>

                    <div className="inv-mix" aria-label="Status overview">
                        <div className="inv-mix-bar">
                            {inventoryMix.filter((status) => !status.isTotal && status.pct > 0).map((status) => (
                                <span
                                    key={status.name}
                                    style={{ width: `${status.pct}%`, background: status.color }}
                                    title={`${status.name} ${formatInventoryPct(status.pct)} of stock on hand`}
                                />
                            ))}
                        </div>
                        <div className="inv-mix-pills">
                            {inventoryItems.length === 0 && (
                                <span className="inv-mix-empty">No stock status in this warehouse.</span>
                            )}
                            {inventoryMix.map((status) => (
                                <span className={`inv-mix-pill${status.isTotal ? " is-total" : ""}`} key={status.name}>
                                    <i style={{ background: status.color }} />
                                    <span>{status.name}</span>
                                    <strong>{formatQty(status.isTotal ? status.value : status.qty)}</strong>
                                    <em>{formatInventoryPct(status.pct)}</em>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="dash-table-wrap dash-inventory-wrap">
                        <table className="dash-table dash-inventory-table">
                            <thead>
                                <tr>
                                    <th>Facility Name</th>
                                    <th>Owner</th>
                                    <th>SKU ID</th>
                                    <th>Description</th>
                                    <th>UOM</th>
                                    <th className="dash-num">Stock On Hand</th>
                                    <th className="dash-num">Allocated</th>
                                    <th className="dash-num">Picked</th>
                                    <th className="dash-num">On Hold</th>
                                    <th className="dash-num">Available</th>
                                </tr>
                                <tr className="dash-inventory-filter-row">
                                    <th>
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.facilityName}
                                            onChange={(e) => updateInventoryColumnFilter("facilityName", e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.owner}
                                            onChange={(e) => updateInventoryColumnFilter("owner", e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.item}
                                            onChange={(e) => updateInventoryColumnFilter("item", e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.description}
                                            onChange={(e) => updateInventoryColumnFilter("description", e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.unitOfMeasure}
                                            onChange={(e) => updateInventoryColumnFilter("unitOfMeasure", e.target.value)}
                                        />
                                    </th>
                                    <th className="dash-num">
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.stockOnHand}
                                            onChange={(e) => updateInventoryColumnFilter("stockOnHand", e.target.value)}
                                        />
                                    </th>
                                    <th className="dash-num">
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.allocated}
                                            onChange={(e) => updateInventoryColumnFilter("allocated", e.target.value)}
                                        />
                                    </th>
                                    <th className="dash-num">
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.picked}
                                            onChange={(e) => updateInventoryColumnFilter("picked", e.target.value)}
                                        />
                                    </th>
                                    <th className="dash-num">
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.onHold}
                                            onChange={(e) => updateInventoryColumnFilter("onHold", e.target.value)}
                                        />
                                    </th>
                                    <th className="dash-num">
                                        <input
                                            type="text"
                                            className="dash-col-filter"
                                            placeholder=""
                                            value={inventoryColumnFilters.available}
                                            onChange={(e) => updateInventoryColumnFilter("available", e.target.value)}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInventoryItems.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="dash-table-empty">No inventory for this warehouse.</td>
                                    </tr>
                                )}
                                {filteredInventoryItems.map((item) => (
                                    <tr key={item.id} className={Number(item.onHold) > 0 ? "is-hold" : ""}>
                                        <td>{item.facilityName}</td>
                                        <td>{item.owner}</td>
                                        <td className="inv-sku">{item.item}</td>
                                        <td>{item.description}</td>
                                        <td>{item.unitOfMeasure}</td>
                                        <td className="dash-num">{formatQty(item.stockOnHand)}</td>
                                        <td className="dash-num">{formatQty(item.allocated)}</td>
                                        <td className="dash-num">{formatQty(item.picked)}</td>
                                        <td className={`dash-num${Number(item.onHold) > 0 ? " is-hold-qty" : ""}`}>{formatQty(item.onHold)}</td>
                                        <td className="dash-num">{formatQty(item.available)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
            <div className="dash-split">
                <div className="dash-panel">
                    <h4 className="card-title">{topStatuses.length ? `Top ${topStatuses.length} by volume` : "Top statuses"}</h4>
                    <p className="dash-muted">Largest share of {dataConfig.totalLabel.toLowerCase()}</p>

                    {topStatuses.length === 0 && (
                        <div className="dash-chart-empty">No status volume in this range.</div>
                    )}

                    <div className="dash-rank-list">
                        {topStatuses.map((status, index) => {
                            const pct = totalStatus ? (status.value / totalStatus) * 100 : 0;
                            const stage = statusStage(status.name, dataView);
                            return (
                                <div className="dash-rank" key={status.name}>
                                    <span className="dash-rank-index" style={{ color: status.color, background: `${status.color}1A` }}>
                                        {index + 1}
                                    </span>
                                    <div className="dash-rank-body">
                                        <div className="dash-rank-name">{status.name}</div>
                                        <div className="dash-muted">{formatQty(status.value)} {dataConfig.unit}</div>
                                    </div>
                                    <div className={`dash-rank-pct dash-kpi-delta-${stage === "cancelled" ? "down" : stage === "waiting" ? "watch" : "up"}`}>
                                        {pct.toFixed(0)}%
                                        <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="dash-panel">
                    <div className="dash-panel-head">
                        <div>
                            <h4 className="card-title">{dataConfig.lineTitle}</h4>
                            {trend.caption && <p className="dash-muted">{trend.caption}</p>}
                        </div>
                        <div className="dash-legend">
                            {trend.series.map((series) => (
                                <span key={series.key}>
                                    <i style={{ background: series.color }} />
                                    {series.key}
                                </span>
                            ))}
                        </div>
                    </div>

                    {trend.data.length === 0 ? (
                        <div className="dash-chart-empty">No trend for this range yet.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={trend.data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                                <defs>
                                    {trend.series.map((series) => (
                                        <linearGradient id={trendGradientId(series.key)} key={series.key} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={series.color} stopOpacity={0.32} />
                                            <stop offset="100%" stopColor={series.color} stopOpacity={0.02} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12, fill: "#6b7280" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 12, fill: "#6b7280" }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={48}
                                    tickFormatter={compactNumber}
                                />
                                <Tooltip
                                    content={(props) => <TrendTooltip {...props} currency={isCurrency} />}
                                />
                                {trend.series.map((series) => (
                                    <Area
                                        key={series.key}
                                        type="monotone"
                                        dataKey={series.key}
                                        stroke={series.color}
                                        strokeWidth={2.5}
                                        fill={`url(#${trendGradientId(series.key)})`}
                                        dot={false}
                                        activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                                        animationDuration={900}
                                        animationEasing="ease-out"
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
            )}
            </div>
            </div>
        </div>
    );
};

export default TenantDashboard;
